import {useQuery} from '@tanstack/react-query';
import {ResponseError as PressReleaseResponseError} from '@openapi/press-release';
import {getPressReleaseForPreview} from '../operations/get-press-release-for-preview';
import {getPressReleaseForExternalPreview} from '../operations/get-press-release-for-external-preview';
import {useSetShouldShowPasswordForm} from '../../context/should-show-password-form';
import {useSetIsPasswordInvalid} from '../../context/is-password-invalid';
import {getParameters} from '../../lib/parameters';
import {transform} from '../../lib/transform';
import {usePreviewPagePasswordValue} from '../../context/password';

/**
 * プレビューページ・外部共有プレビューページで表示するプレスリリースのクエリ
 */
export const usePressReleaseQuery = () => {
  const urlParameters = getParameters();

  /** パスワードフォームで入力されたパスワード */
  const previewPagePassword = usePreviewPagePasswordValue();

  const setIsPasswordInvalid = useSetIsPasswordInvalid();

  const setShouldShowPasswordForm = useSetShouldShowPasswordForm();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['pressRelease', previewPagePassword],
    async queryFn() {
      // プレビューページの時、releaseId と companyId でデータを取得
      if (urlParameters.page === 'preview') {
        const response = await getPressReleaseForPreview({
          releaseId: urlParameters.releaseId,
          companyId: urlParameters.companyId,
        });
        const isOldRelease = response.editorVersion === null;

        return {
          ...response,
          text: transform(response.text, isOldRelease),
        };
      }

      // 外部共有プレビューページの時、key と入力したパスワードでデータを取得
      if (urlParameters.page === 'external-preview') {
        try {
          const response = await getPressReleaseForExternalPreview({
            previewPageKey: urlParameters.previewPageKey,
            previewPagePassword,
          });
          const isOldRelease = response.editorVersion === null;

          return {
            ...response,
            text: transform(response.text, isOldRelease),
          };
        } catch (error) {
          if (
            error instanceof PressReleaseResponseError &&
            error.response.status === 401
          ) {
            if (previewPagePassword === null) {
              // パスワード null のリクエストで 401 が返ってきた場合、パスワードフォームを表示する
              setShouldShowPasswordForm(true);
            } else {
              // パスワードありのリクエストで 401 が返ってきた場合、パスワードが間違っているとしてエラーを表示する
              setIsPasswordInvalid(true);
            }
          }

          throw error;
        }
      }

      throw new Error('Invalid query status');
    },
  });

  return query;
};
